import type { SwmmProject } from "../inp/types.ts";
import type { TextKey } from "../../i18n/en.ts";
import type { AreaOfUse } from "./crs.ts";
import { toLonLat } from "./reproject.ts";

interface CrsValidationResult {
  valid: boolean;
  reason?: TextKey;
}

// Generous on purpose: this catches "wrong projection entirely" (a
// different continent), not "off by a few km near a zone boundary".
const AREA_OF_USE_MARGIN_DEG = 5;

/**
 * proj4 itself won't throw for most wrong picks — a UTM formula is defined
 * for any real input, it'll just compute a plausible-looking but WRONG
 * location. Checking only "is the reprojected point a finite, in-range
 * lon/lat" would miss that — a wrong UTM zone/datum/hemisphere still
 * reprojects to a valid-looking coordinate. The area-of-use centroid check
 * below is what actually catches that case.
 */
export function validateCrs(
  project: SwmmProject,
  proj4Str: string,
  areaOfUse?: AreaOfUse
): CrsValidationResult {
  const points = [
    ...project.coordinates.values(),
    ...[...project.vertices.values()].flat(),
    ...[...project.polygons.values()].flat(),
    ...project.symbols.values(),
  ];
  if (points.length === 0) return { valid: true }; // nothing to check against

  let sumLon = 0;
  let sumLat = 0;
  for (const point of points) {
    let lon: number;
    let lat: number;
    try {
      [lon, lat] = toLonLat(point, proj4Str);
    } catch {
      return { valid: false, reason: "crsValidation.cantConvert" };
    }
    if (
      !Number.isFinite(lon) ||
      !Number.isFinite(lat) ||
      Math.abs(lon) > 180 ||
      Math.abs(lat) > 90
    ) {
      return { valid: false, reason: "crsValidation.invalidRange" };
    }
    sumLon += lon;
    sumLat += lat;
  }

  if (areaOfUse) {
    const centroidLon = sumLon / points.length;
    const centroidLat = sumLat / points.length;
    const inArea =
      centroidLon >= areaOfUse.west - AREA_OF_USE_MARGIN_DEG &&
      centroidLon <= areaOfUse.east + AREA_OF_USE_MARGIN_DEG &&
      centroidLat >= areaOfUse.south - AREA_OF_USE_MARGIN_DEG &&
      centroidLat <= areaOfUse.north + AREA_OF_USE_MARGIN_DEG;
    if (!inArea) {
      return { valid: false, reason: "crsValidation.outsideAreaOfUse" };
    }
  }

  return { valid: true };
}
